/**
 * ParentFinancePortal.js
 *
 * Parent portal page for viewing children's financial information.
 * Similar to BOT Clearance Portal but focused on finances.
 *
 * Features:
 * - View list of admitted children
 * - Click on child to view complete financial details
 * - Read-only access (parents cannot modify data)
 *
 * @module ParentFinancePortal
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Empty,
  Spin,
  Typography,
  Space,
  Statistic,
  Tag,
  message,
} from "antd";
import {
  UserOutlined,
  DollarOutlined,
  WalletOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";
import StudentFinanceDrawer from "../../components/finance/StudentFinanceDrawer";

const { Title, Text } = Typography;

/**
 * ParentFinancePortal Component
 *
 * Displays children linked to the current parent user and allows
 * viewing their complete financial information.
 *
 * @component
 */
const ParentFinancePortal = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  /**
   * Fetch admitted students linked to current user
   * Uses the same endpoint as BOT Clearance Portal
   */
  const fetchMyStudents = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${domainSettings.urls.enrollment.admission}?status=ADMITTED`;
      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );

      if (response.ok) {
        const admittedStudents = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : [];

        // Filter to only include students with valid SPC
        const validStudents = admittedStudents.filter(
          (admission) => admission.student && admission.student.spcCode,
        );

        setStudents(validStudents);
      } else {
        message.error("Failed to load students");
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      message.error("An error occurred while loading students");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchMyStudents();
  }, [fetchMyStudents]);

  /**
   * Handle viewing finances for a specific student
   * Opens the finance drawer with student details
   *
   * @param {object} admission - The admission record containing student info
   */
  const handleViewFinances = (admission) => {
    const student = admission.student;
    setSelectedStudent({
      spc: student.spcCode || student.spc,
      fullName:
        `${student.firstName || ""} ${student.middleName || ""} ${student.lastName || ""}`.trim(),
      firstName: student.firstName,
      lastName: student.lastName,
      className: student.className || student.class_name,
      acadyr: student.acadyr || student.current_acadyr,
      dayBoarding: student.dayBoarding || student.day_boarding,
    });
    setDrawerVisible(true);
  };

  /**
   * Handle closing the finance drawer
   */
  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setSelectedStudent(null);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Spin
          size="large"
          indicator={<LoadingOutlined spin />}
          tip="Loading your children's information..."
        />
      </div>
    );
  }

  return (
    <>
      <div style={{ padding: "24px" }}>
        <Card>
          {/* Header */}
          <div style={{ marginBottom: 32 }}>
            <Space direction="vertical" size="small">
              <Title level={3} style={{ margin: 0 }}>
                <WalletOutlined /> My Finances
              </Title>
              <Text type="secondary">
                View complete financial information for each of your children
              </Text>
            </Space>
          </div>

          {/* No Students Message */}
          {students.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <Text>No children linked to your account</Text>
                  <Text type="secondary">
                    Please contact the school administration to link your
                    children to your account
                  </Text>
                </Space>
              }
            />
          ) : (
            <>
              {/* Summary Statistics */}
              <div style={{ marginBottom: 24 }}>
                <Statistic
                  title="Total Children"
                  value={students.length}
                  prefix={<UserOutlined />}
                />
              </div>

              {/* Students Grid */}
              <Row gutter={[16, 16]}>
                {students.map((admission) => {
                  const student = admission.student;
                  const fullName =
                    `${student.firstName || ""} ${student.middleName || ""} ${student.lastName || ""}`.trim();

                  return (
                    <Col
                      xs={24}
                      sm={12}
                      md={8}
                      lg={6}
                      key={student.spcCode || student.id}
                    >
                      <Card
                        hoverable
                        onClick={() => handleViewFinances(admission)}
                        style={{
                          cursor: "pointer",
                          borderRadius: "8px",
                          transition: "all 0.3s",
                          height: "100%",
                        }}
                        bodyStyle={{ padding: "20px" }}
                      >
                        <Space
                          direction="vertical"
                          style={{ width: "100%" }}
                          size="middle"
                        >
                          {/* Icon */}
                          <div style={{ textAlign: "center" }}>
                            <UserOutlined
                              style={{
                                fontSize: "48px",
                                color: "#1890ff",
                                marginBottom: "8px",
                              }}
                            />
                          </div>

                          {/* Student Name */}
                          <div>
                            <Title
                              level={5}
                              style={{ marginBottom: 4, textAlign: "center" }}
                            >
                              {fullName}
                            </Title>

                            <div style={{ textAlign: "center" }}>
                              <Tag color="blue">
                                {student.spcCode || student.spc}
                              </Tag>
                            </div>
                          </div>

                          {/* Student Details */}
                          <div
                            style={{
                              borderTop: "1px solid #f0f0f0",
                              paddingTop: "12px",
                              textAlign: "center",
                            }}
                          >
                            <Space
                              direction="vertical"
                              size="small"
                              style={{ width: "100%" }}
                            >
                              <Text strong>
                                {student.className || student.class_name}
                              </Text>
                              <Text type="secondary">
                                {student.acadyr || student.current_acadyr}
                              </Text>
                              <Tag
                                color={
                                  (student.dayBoarding ||
                                    student.day_boarding) === "Boarding"
                                    ? "green"
                                    : "orange"
                                }
                              >
                                {student.dayBoarding || student.day_boarding}
                              </Tag>
                            </Space>
                          </div>

                          {/* Action Hint */}
                          <div
                            style={{
                              textAlign: "center",
                              paddingTop: "8px",
                            }}
                          >
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              <DollarOutlined /> Click to view finances
                            </Text>
                          </div>
                        </Space>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </>
          )}
        </Card>

        {/* Student Finance Drawer */}
        <StudentFinanceDrawer
          visible={drawerVisible}
          onClose={handleCloseDrawer}
          studentSpc={selectedStudent?.spc}
          studentName={selectedStudent?.fullName || ""}
          readOnly={true}
        />
      </div>
    </>
  );
};

export default ParentFinancePortal;
